import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlCreateErrorLog } from '../../Constants';
import { IAction, SliceProps } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IGeneralErrorState = {
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    generalError: async (config: { error: Error; replicaId: string }): Promise<void> => {
        try {
            const { error, replicaId } = config;

            const formData = new FormData();
            formData.append(
                'errorData',
                JSON.stringify({
                    errorFreeText: error.stack,
                }),
            );

            const request: RequestInit = {
                method: 'POST',
                body: formData,
                headers: { replicaId },
            };

            const response = await apiFetch(urlCreateErrorLog, request);

            return response.ok
                ? Promise.resolve()
                : Promise.reject(new Error(await response.text()));
        } catch (e: unknown) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IGeneralErrorState>) => {
        /**
         * Reducers for the 'postGeneralError' thunk.
         */
        builder.addCase(generalError.pending, (state: Draft<IGeneralErrorState>) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(generalError.fulfilled, (state: Draft<IGeneralErrorState>) => {
            state.loading = false;
            state.error = null;
        });

        builder.addCase(
            generalError.rejected,
            (state: Draft<IGeneralErrorState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

export const generalError = createAsyncThunk(urlCreateErrorLog, thunks.generalError);

/**
 * Interface that describes the state object for this redux slice.
 */
export type IGeneralErrorState = SliceProps;

/**
 * Create the redux slice.
 */
export const generalErrorSlice = createSlice({
    name: 'generalError',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export slice reducer as default.
 */
export default generalErrorSlice.reducer;
